// Plan & Billing — the restaurant's plan as the server reports it, the
// upgrade path for a Basic restaurant only, an annual "Keep Premium" for a
// complimentary plan that ends, the Customer Portal for a paying one,
// and the "I already paid" claim for a purchase made before the restaurant
// existed (billing-signup.md §5.4). Admin-only via the registry's roleGate.

import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Loader2, CheckCircle, Sparkles, ExternalLink } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
// The amber surface, and its dark variant, live in the library. A call site
// that spells a raw Tailwind palette class names a colour that does not flip
// with the theme, which is what the token guard refuses.
import { TONE_SURFACE } from '@synvya/portal-ui/cards'
import { useBilling } from '@/state/useBilling'
import { useAuth } from '@/state/useAuth'
import { useLoyaltyContext } from '@/hooks/useLoyaltyContext'
import { formatPlanDate } from '@/components/GrandfatherBanner'
import {
  claimPurchase,
  createPortalSession,
  createUpgradeCheckout,
} from '@/services/billing'
import type { BillingInterval } from '@/services/billing'
import { ApiError } from '@/lib/apiError'
import { describeFailure } from '@/lib/describeFailure'

/** How long the page keeps asking after a Checkout success before giving up. */
const ACTIVATION_POLL_MS = 2_000
const ACTIVATION_MAX_POLLS = 15

export default function PlanBillingPage() {
  const { t } = useTranslation('billing')
  const ctx = useLoyaltyContext()
  const email = useAuth((s) => s.email)
  const personalPubkey = useAuth((s) => s.personalPubkey)
  const billing = useBilling()
  const [params, setParams] = useSearchParams()

  const [interval, setInterval_] = useState<BillingInterval>('year')
  const [busy, setBusy] = useState<'checkout' | 'portal' | 'claim' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  // Returning from an upgrade Checkout: the webhook may land a second or two
  // after the redirect, so ask again until the plan flips or patience runs out.
  const checkoutParam = params.get('checkout')
  const [activating, setActivating] = useState(checkoutParam === 'success')
  const polls = useRef(0)
  useEffect(() => {
    if (!activating || !ctx) return
    if (billing.loaded && billing.plan === 'premium') {
      setActivating(false)
      setParams({}, { replace: true })
      return
    }
    if (polls.current >= ACTIVATION_MAX_POLLS) {
      setActivating(false)
      setNotice(t('activationSlow'))
      setParams({}, { replace: true })
      return
    }
    const timer = window.setTimeout(() => {
      polls.current += 1
      void billing.refresh(ctx)
    }, ACTIVATION_POLL_MS)
    return () => window.clearTimeout(timer)
  }, [activating, ctx, billing, setParams, t])

  useEffect(() => {
    if (checkoutParam === 'cancelled') {
      setNotice(t('checkoutCancelled'))
      setParams({}, { replace: true })
    }
  }, [checkoutParam, setParams, t])

  const handleUpgrade = useCallback(async () => {
    if (!ctx) return
    setBusy('checkout')
    setError(null)
    try {
      const { url } = await createUpgradeCheckout(ctx, interval, email)
      window.location.assign(url)
    } catch (err) {
      setError(describeFailure(err, t('checkoutFailed')))
      setBusy(null)
    }
  }, [ctx, interval, email, t])

  const handleKeep = useCallback(async () => {
    if (!ctx) return
    setBusy('checkout')
    setError(null)
    try {
      const { url } = await createUpgradeCheckout(ctx, 'year', email)
      window.location.assign(url)
    } catch (err) {
      setError(describeFailure(err, t('checkoutFailed')))
      setBusy(null)
    }
  }, [ctx, email, t])

  const handlePortal = useCallback(async () => {
    if (!ctx) return
    setBusy('portal')
    setError(null)
    try {
      const { url } = await createPortalSession(ctx)
      window.location.assign(url)
    } catch (err) {
      setError(describeFailure(err, t('checkoutFailed')))
      setBusy(null)
    }
  }, [ctx, t])

  const handleClaim = useCallback(async () => {
    if (!ctx || !personalPubkey) return
    setBusy('claim')
    setError(null)
    try {
      const snapshot = await claimPurchase(ctx, personalPubkey)
      billing.apply(ctx.restaurantPubkey, snapshot)
    } catch (err) {
      if (err instanceof ApiError && err.code === 'not_found') {
        setError(t('claimNoPurchase'))
      } else {
        setError(describeFailure(err, t('checkoutFailed')))
      }
    } finally {
      setBusy(null)
    }
  }, [ctx, personalPubkey, billing, t])

  const isPremium = billing.loaded && billing.plan === 'premium'
  const viaStripe = isPremium && billing.source === 'stripe'
  const viaComp = isPremium && billing.source === 'comp'

  return (
    <div className="space-y-4 px-4 py-4">
      <h1 className="text-2xl font-bold text-foreground">{t('pageTitle')}</h1>

      {activating && (
        <Card data-testid="plan-activating" className="flex items-center gap-3">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          <p className="text-sm text-foreground">{t('activating')}</p>
        </Card>
      )}

      {notice && (
        <p role="status" className="text-sm text-muted-foreground">{notice}</p>
      )}

      {/* Current plan */}
      <Card data-testid="plan-current">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t('currentPlan')}</p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <p className="text-lg font-bold text-foreground">
            {isPremium ? t('premiumName') : t('basicName')}
          </p>
          <PlanPill />
        </div>
        {viaStripe && (billing.cancelAt || billing.currentPeriodEnd) && (
          <p className="mt-1 text-sm text-muted-foreground" data-testid="plan-period">
            {billing.cancelAtPeriodEnd
              ? t('endsOn', { date: formatPlanDate(billing.cancelAt ?? billing.currentPeriodEnd!) })
              : t('renewsOn', { date: formatPlanDate(billing.currentPeriodEnd!) })}
            {billing.interval && ` · ${t(billing.interval === 'year' ? 'billedAnnually' : 'billedMonthly')}`}
          </p>
        )}
        {viaComp && billing.compSource && (
          <p className="mt-1 text-sm text-muted-foreground">
            {t('compProvided', { source: t(`compSource.${billing.compSource}`) })}
            {billing.compExpiresAt && ` · ${t('endsOn', { date: formatPlanDate(billing.compExpiresAt) })}`}
          </p>
        )}
        {!billing.loaded && !billing.loading && billing.error && (
          <p className="mt-2 text-sm text-destructive">{billing.error}</p>
        )}
      </Card>

      {/* Upgrade — Basic only. A Premium restaurant, paid or complimentary, is
          never asked to upgrade to the plan it already has. */}
      {billing.loaded && !isPremium && (
        <Card data-testid="plan-upgrade">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <p className="text-sm font-semibold text-foreground">{t('premiumName')}</p>
          </div>
          <ul className="mt-3 space-y-1 text-sm text-muted-foreground">
            {(['happyHours', 'events', 'offers', 'pulse', 'printKit', 'takeout'] as const).map((k) => (
              <li key={k}>✓ {t(`premiumFeatures.${k}`)}</li>
            ))}
          </ul>
          <IntervalToggle value={interval} onChange={setInterval_} />
          <p className="mt-2 text-xs text-muted-foreground">{t('plusTax')}</p>
          <Button className="mt-3 w-full" onClick={handleUpgrade} disabled={!ctx || busy !== null}>
            {busy === 'checkout' ? <Loader2 className="h-4 w-4 animate-spin" /> : t('upgrade')}
          </Button>
          <div className="mt-4 border-t border-border pt-3">
            <p className="text-xs text-muted-foreground">{t('alreadyPaidHint')}</p>
            <button
              type="button"
              onClick={handleClaim}
              disabled={!ctx || !personalPubkey || busy !== null}
              className="mt-1 text-sm font-medium text-primary hover:underline disabled:opacity-50"
            >
              {busy === 'claim' ? t('activating') : t('alreadyPaid')}
            </button>
          </div>
        </Card>
      )}

      {/* Keep Premium — a complimentary plan with an end date can subscribe
          before it lapses. Annual only: a Premium restaurant is not offered the
          monthly plan while no downgrade path exists. An open-ended comp has
          nothing to keep, so it gets no card. */}
      {viaComp && billing.compExpiresAt && (
        <Card data-testid="plan-keep">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            <p className="text-sm font-semibold text-foreground">{t('keepTitle')}</p>
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            {t('keepBody', { date: formatPlanDate(billing.compExpiresAt) })}
          </p>
          <p className="mt-3 text-sm text-foreground">
            {t('priceAnnual')} {t('perYear')}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">{t('plusTax')}</p>
          <Button className="mt-3 w-full" onClick={handleKeep} disabled={!ctx || busy !== null}>
            {busy === 'checkout' ? <Loader2 className="h-4 w-4 animate-spin" /> : t('keepAction')}
          </Button>
        </Card>
      )}

      {/* Manage — anything with a Stripe customer */}
      {billing.loaded && billing.hasStripeCustomer && (
        <Card data-testid="plan-manage">
          <p className="text-sm font-semibold text-foreground">{t('manageBilling')}</p>
          <p className="mt-1 text-xs text-muted-foreground">{t('manageBillingHint')}</p>
          <Button variant="secondary" className="mt-3" onClick={handlePortal} disabled={!ctx || busy !== null}>
            {busy === 'portal' ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
            {t('manageBilling')}
          </Button>
        </Card>
      )}

      {error && (
        <p role="alert" className="text-sm text-destructive">{error}</p>
      )}
    </div>
  )
}

function PlanPill() {
  const { t } = useTranslation('billing')
  const { loaded, loading, plan, source, status, cancelAtPeriodEnd, cancelAt, currentPeriodEnd, compExpiresAt } = useBilling()
  if (!loaded) {
    return loading ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : null
  }
  if (plan === 'basic') return <Badge variant="soft" className="bg-muted text-muted-foreground">{t('pillBasic')}</Badge>
  if (source === 'stripe' && status === 'past_due') {
    return <Badge className={TONE_SURFACE.amber}>{t('pillPastDue')}</Badge>
  }
  if (source === 'stripe' && cancelAtPeriodEnd && (cancelAt || currentPeriodEnd)) {
    return <Badge className={TONE_SURFACE.amber}>{t('pillCancels', { date: formatPlanDate(cancelAt ?? currentPeriodEnd!) })}</Badge>
  }
  if (source === 'comp' && compExpiresAt) {
    return <Badge variant="soft">{t('pillTrialEnds', { date: formatPlanDate(compExpiresAt) })}</Badge>
  }
  return <Badge>{t('pillPremium')}</Badge>
}

export function IntervalToggle({
  value,
  onChange,
}: {
  value: BillingInterval
  onChange: (v: BillingInterval) => void
}) {
  const { t } = useTranslation('billing')
  const options: { key: BillingInterval; label: string; price: string; per: string }[] = [
    { key: 'month', label: t('toggleMonthly'), price: t('priceMonthly'), per: t('perMonth') },
    { key: 'year', label: t('toggleAnnual'), price: t('priceAnnual'), per: t('perYear') },
  ]
  return (
    <div className="mt-3 grid grid-cols-2 gap-2" role="radiogroup">
      {options.map((opt) => (
        <button
          key={opt.key}
          type="button"
          role="radio"
          aria-checked={value === opt.key}
          onClick={() => onChange(opt.key)}
          className={`rounded-lg border px-3 py-2 text-left text-sm transition-colors ${
            value === opt.key
              ? 'border-primary bg-primary/10 text-foreground'
              : 'border-border bg-background text-muted-foreground hover:text-foreground'
          }`}
        >
          <span className="block font-medium">{opt.label}</span>
          <span className="block text-xs">
            {opt.price} {opt.per}
            {opt.key === 'year' && ` · ${t('annualSaving')}`}
          </span>
          {opt.key === 'year' && value === 'year' && <CheckCircle className="mt-1 h-3.5 w-3.5 text-primary" />}
        </button>
      ))}
    </div>
  )
}
